import { useMemo } from "react";
import type { CSSProperties } from "react";
import { Stack } from "@mantine/core";
import { ReportPhotoCaption } from "./ReportPhotoCaption";
import { ReportMediaFrame } from "./ReportMediaFrame";
import { ReportPhotoMedia } from "./ReportPhotoMedia";

export interface ReportImageAttachment {
  id: string;
  caption?: string;
  magnification?: string;
  fileName?: string;
}

export interface ReportPhotoLayout {
  frameHeight?: CSSProperties["height"];
  height?: CSSProperties["height"];
  maxHeight?: CSSProperties["maxHeight"];
  maxWidth?: CSSProperties["maxWidth"];
  objectPosition?: CSSProperties["objectPosition"];
  width: CSSProperties["width"];
}

export interface ReportPhotoContentProps {
  image: ReportImageAttachment;
  url?: string;
  failed?: boolean;
  onLoadError?: (imageId: string) => void;
  onRetry?: (imageId: string) => void;
  layout: ReportPhotoLayout;
}

export type OrganMapPhoto = Omit<ReportPhotoContentProps, "layout">;

const getCaption = (image: ReportImageAttachment): string | undefined =>
  image.caption?.trim() || undefined;

const getMetadata = (image: ReportImageAttachment, caption?: string): string =>
  [caption, image.magnification?.trim()].filter(Boolean).join(" — ");

const getAlt = (image: ReportImageAttachment, caption?: string): string =>
  caption || image.fileName || "Case image";

export function ReportPhotoContent(props: ReportPhotoContentProps) {
  const { image, layout } = props;
  const caption = useMemo(() => getCaption(image), [image]);
  const metadata = useMemo(() => getMetadata(image, caption), [caption, image]);
  const alt = useMemo(() => getAlt(image, caption), [caption, image]);

  return (
    <Stack align="center" gap={0} w="100%">
      <ReportMediaFrame height={layout.frameHeight}>
        <ReportPhotoMedia {...props} alt={alt} />
      </ReportMediaFrame>
      <ReportPhotoCaption caption={caption} metadata={metadata} />
    </Stack>
  );
}
